import { useCallback, useEffect, useLayoutEffect, useState } from "react";

/**
 * Measures the combined offsetHeight of DOM elements by their IDs.
 * Recalculates on mount, resize, and when the element list changes.
 *
 * @param elementIds - Array of DOM element IDs to measure
 * @returns Combined height in pixels
 *
 * @example
 * const topOffset = useCombinedElementHeight(["navbar", "progress-bar"]);
 */
export function useCombinedElementHeight(elementIds?: string[]): number {
  const [height, setHeight] = useState(0);

  const measure = useCallback(() => {
    if (!elementIds?.length) return;
    const total = elementIds.reduce((sum, id) => {
      const el = document.getElementById(id);
      return sum + (el?.offsetHeight ?? 0);
    }, 0);
    setHeight(total);
  }, [elementIds]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!elementIds?.length) return undefined;
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [elementIds, measure]);

  return height;
}
